import { isEqual, map, isNumber, isBoolean, find, isString } from 'lodash-es';
import {
  createRouter,
  createWebHashHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
} from 'vue-router';
import { type App, type Component } from 'vue';
import debug from 'debug';
import { RouteSpec } from './schemas';

const log = debug('itp:core:routes');

const __specs__: RouteSpec[] = [];

export function addRoute(config: RouteSpec) {
  log('addRoute: %o', config);
  __specs__.push(config);
}

function resolveLazy(
  value: string | (() => string) | undefined,
): string | undefined {
  if (typeof value === 'function') {
    try {
      const resolved = value();
      return typeof resolved === 'string' ? resolved : undefined;
    } catch (e) {
      log('failed to resolve lazy route value:', e);
      return undefined;
    }
  }
  return value;
}

export function getRouter(home: string, app: App) {
  const specs = __specs__;

  if (!home) {
    console.error('Home component name is missing!');
    home = 'Dashboard';
  }

  const routes: RouteRecordRaw[] = buildTopLevelRoutes(specs, app);

  const router = createRouter({
    history: createWebHashHistory(),
    routes,
  });

  return router;
}

const resolveAppComponent = (name: string, app: App): Component => {
  const comp = app.component(name);
  if (!comp) {
    console.warn(`Component "${name}" not found in app registry.`);
    return {
      name: `NotFound_${name}`,
      template: `<div class="alert alert-danger">Component "${name}" not found</div>`,
    };
  }
  return comp;
};

const buildTopLevelRoutes = (specs: RouteSpec[], app: App): RouteRecordRaw[] => {
  const rootSpecs = specs.filter((p) => p.name && p.name.indexOf('.') === -1);

  return rootSpecs.map((spec) => {
    const specName = spec.name || 'unnamed';
    const routeName = `app.${specName}`;
    const compName = spec.component || 'EmptyComponent';

    const route: RouteRecordRaw = {
      path: spec.path,
      name: routeName,
      component: resolveAppComponent(compName, app),
      props: componentPropsGetter(spec),
      meta: {
        title: resolveLazy(spec.title) || '..',
        icon: resolveLazy(spec.icon),
        closable: spec.closable !== false,
        key: getTabKey(spec, specs),
      },
      children: buildRoutes({ name: specName }, specs, app),
    };

    return route;
  });
};


const buildRoutes = (
  parent: { name: string },
  specs: RouteSpec[],
  app: App,
): RouteRecordRaw[] => {
  const children = findChildren(parent.name, specs);
  return map(children, (spec) => buildRouteConfig(parent, spec, specs, app));
};

const buildRouteConfig = (
  parent: { name: string },
  spec: RouteSpec,
  all: RouteSpec[],
  app: App,
): RouteRecordRaw => {
  const specName = spec.name || 'unnamed';
  const routeName = `app.${specName}`;

  const compName = spec.component || 'EmptyComponent';

  const result: RouteRecordRaw = {
    path: spec.path,
    name: routeName,
    component: resolveAppComponent(compName, app),
    props: componentPropsGetter(spec),
    meta: {
      title: resolveLazy(spec.title) || '..',
      icon: resolveLazy(spec.icon),
      closable: spec.closable !== false,
      key: getTabKey(spec, all),
    },
    children: [],
  };

  result.children = buildRoutes({ name: specName }, all, app);
  return result;
};

const componentPropsGetter = (spec: RouteSpec) => {
  return function getProps(route: RouteLocationNormalized) {
    const result: Record<string, any> = { ...route.params };

    if (spec.params) {
      for (const p of spec.params) {
        if (route.params && p.name in route.params) {
          result[p.prop] = route.params[p.name];
          continue;
        }
        if (route.query && p.name in route.query) {
          const value = route.query[p.name];
          if (isString(value) || isNumber(value) || isBoolean(value)) {
            result[p.prop] = value.toString();
          }
        }
      }
    }

    if (spec.componentProps) {
      for (let item of spec.componentProps) {
        result[item.name] = item.value;
      }
    }
    return result;
  };
};

const getTabKey = (spec: RouteSpec, all: RouteSpec[]) => {
  if (!spec.name) return 'path';
  const parts = spec.name.split('.');

  if (parts.length > 1) {
    const p0 = parts[0];
    const parent = find(all, (p) => p.name === p0);
    if (parent) return parent.path;
  }

  const hasChildren = all.some(
    (p) => p.name && p.name.startsWith(spec.name + '.'),
  );
  if (hasChildren) return spec.path;

  return 'path';
};

const findChildren = (name: string, specs: RouteSpec[]): RouteSpec[] => {
  if (!name || name === 'root') {
    return specs.filter((p) => p.name && p.name.indexOf('.') === -1);
  }
  const parts = name.split('.');
  return specs.filter((p) => {
    if (!p.name || p.name.indexOf('.') === -1) return false;
    const thisParts = p.name.split('.');
    if (thisParts.length !== parts.length + 1) return false;
    return isEqual(parts, thisParts.slice(0, parts.length));
  });
};
